import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import { Auth0Provider } from "@auth0/auth0-react";
import { Provider } from "react-redux";
import * as Sentry from "@sentry/react";
import {
  GlobalStyles,
  StyledEngineProvider,
  ThemeProvider,
} from "@mui/material";
import theme from "./theme";
import { router } from "./router";
import { AuthProvider } from "./contexts/AuthContext";
import { store } from "./store";
import { logRTKQueryDiagnostics } from "./store/api-diagnostics";
import ErrorBoundary from "./components/common/ErrorBoundary";
import ModalWrapper from "./components/common/ModalWrapper";
import ChartConfigDrawer from "./components/charts/ChartConfigDrawer";
import { initTranslations } from "./utils/i18n"; // Initialize translations
import "./utils/error-logger"; // Initialize global error handlers
import "./config/highcharts-config"; // Initialize Highcharts modules globally
import "./index.css";

console.log("ENV", import.meta.env.VITE_APP_ENV || import.meta.env.MODE);

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.VITE_APP_ENV !== "local",
  environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: import.meta.env.PROD ? 0.2 : 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

// Run diagnostics in development
if (import.meta.env.DEV) {
  logRTKQueryDiagnostics();
}

// Initialize translations
initTranslations().catch(console.error);

createRoot(document.getElementById("root")!).render(
  // <StrictMode>
  <ErrorBoundary>
    <Provider store={store}>
      <Auth0Provider
        domain={import.meta.env.VITE_AUTH0_DOMAIN}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID}
        cacheLocation="localstorage"
        authorizationParams={{
          // redirect_uri: window.location.origin,
          redirect_uri: `${window.location.origin}/callback`,
        }}
      >
        <AuthProvider>
          <StyledEngineProvider enableCssLayer>
            <ThemeProvider theme={theme}>
              <GlobalStyles styles="@layer theme, base, mui, components, utilities;" />
              <RouterProvider router={router} />
              <ModalWrapper />
              <ChartConfigDrawer />
            </ThemeProvider>
          </StyledEngineProvider>
        </AuthProvider>
      </Auth0Provider>
    </Provider>
  </ErrorBoundary>,
  // </StrictMode>,
);
